export const legalDocuments = {
  "privacy": {
    "title": "Privacy Policy",
    "effective": "Effective Date: July 2026",
    "updated": "Last Updated: July 2026",
    "sections": [
      {
        "id": "introduction",
        "number": "1",
        "title": "Introduction",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Welcome to Cullath. This Cullath Privacy Policy explains how we collect, use, store, disclose, and protect your personal information when you access or use our connected workplace platform, including Cullath Connect, Cullath Desk, Cullath Assist, our website, applications, and related services (collectively referred to as the Services)."
          },
          {
            "type": "paragraph",
            "text": "Whether you are an individual user, a business customer, or an authorized user within an organization, this Privacy Policy describes the types of information we collect, why we collect it, how we use it, and the choices available to you regarding your personal data."
          },
          {
            "type": "paragraph",
            "text": "By accessing or using the Cullath Services, you acknowledge that you have read and understood this Privacy Policy. If you do not agree with the practices described in this Policy, you should discontinue using our Services."
          }
        ]
      },
      {
        "id": "information-we-collect",
        "number": "2",
        "title": "Information We Collect",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath collects information to provide, improve, secure, and support our Services. The information we collect depends on how you interact with our platform and may include the following:"
          },
          {
            "type": "subheading",
            "text": "Personal Information"
          },
          {
            "type": "paragraph",
            "text": "When you create an account, contact us, request a demo, or use our Services, we may collect personal information such as:"
          },
          {
            "type": "list",
            "items": [
              "Full name",
              "Email address",
              "Phone number (where applicable)",
              "Company or organization name",
              "Job title or role (where applicable)"
            ]
          },
          {
            "type": "subheading",
            "text": "Account Information"
          },
          {
            "type": "paragraph",
            "text": "To provide access to the Cullath platform, we may collect information related to your account, including:"
          },
          {
            "type": "list",
            "items": [
              "Username and account credentials",
              "Organization or workspace information",
              "User preferences and account settings",
              "Subscription or plan information (where applicable)"
            ]
          },
          {
            "type": "subheading",
            "text": "Device & Technical Information"
          },
          {
            "type": "paragraph",
            "text": "When you access our Services, certain technical information may be collected automatically, including:"
          },
          {
            "type": "list",
            "items": [
              "IP address",
              "Browser type and version",
              "Device type and operating system",
              "Language and time zone settings",
              "Device identifiers (where applicable)",
              "Log files and diagnostic information"
            ]
          },
          {
            "type": "subheading",
            "text": "Usage Information"
          },
          {
            "type": "paragraph",
            "text": "We collect information about how you interact with the Cullath platform to improve performance and user experience. This may include:"
          },
          {
            "type": "list",
            "items": [
              "Pages and features accessed",
              "Login activity",
              "Feature interactions",
              "Usage statistics",
              "Performance analytics",
              "Activity logs",
              "Date and time of access"
            ]
          },
          {
            "type": "subheading",
            "text": "Cookies & Similar Technologies"
          },
          {
            "type": "paragraph",
            "text": "Cullath uses cookies and similar technologies to enhance functionality, improve user experience, remember preferences, analyze website traffic, and support the operation of our Services. Additional information about our use of cookies is provided in the Cookies & Tracking Technologies section of this Privacy Policy."
          }
        ]
      },
      {
        "id": "how-we-use-your-information",
        "number": "3",
        "title": "How We Use Your Information",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath uses the information we collect to provide, maintain, improve, and protect our Services. Depending on how you interact with the platform, your information may be used for the following purposes:"
          },
          {
            "type": "subheading",
            "text": "Account Management"
          },
          {
            "type": "paragraph",
            "text": "We use your information to create and manage user accounts, authenticate users, provide access to the platform, and maintain account settings and preferences."
          },
          {
            "type": "subheading",
            "text": "Service Delivery"
          },
          {
            "type": "paragraph",
            "text": "Your information is used to deliver and operate the Cullath connected workplace platform, including communication, collaboration, remote support, workflow management, and other features available across our Services."
          },
          {
            "type": "subheading",
            "text": "Customer Support"
          },
          {
            "type": "paragraph",
            "text": "We use your information to respond to inquiries, provide technical support, troubleshoot issues, and improve your overall experience with our Services."
          },
          {
            "type": "subheading",
            "text": "Security & Fraud Prevention"
          },
          {
            "type": "paragraph",
            "text": "Your information helps us protect the Cullath platform by detecting unauthorized access, preventing fraudulent or malicious activities, monitoring system performance, and maintaining the security and integrity of our Services."
          },
          {
            "type": "subheading",
            "text": "AI-Powered Features"
          },
          {
            "type": "paragraph",
            "text": "Where AI-powered features are available, information you provide may be processed to generate responses, draft content, summarize information, improve productivity, and deliver the requested AI functionality. AI-generated outputs are intended to assist users and should be reviewed before use."
          },
          {
            "type": "subheading",
            "text": "Service Improvements"
          },
          {
            "type": "paragraph",
            "text": "We analyze usage information, feature interactions, and performance data to improve existing features, develop new functionality, enhance user experience, and optimize the reliability and performance of the Cullath platform."
          },
          {
            "type": "subheading",
            "text": "Communication"
          },
          {
            "type": "paragraph",
            "text": "We may use your contact information to send important service-related notifications, account updates, security alerts, maintenance notices, and other communications necessary for the operation of the Services."
          }
        ]
      },
      {
        "id": "ai-features-data-processing",
        "number": "4",
        "title": "AI Features & Data Processing",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath offers AI-powered features across its Services to assist users with tasks such as drafting content, improving communication, summarizing information, and enhancing productivity. When you choose to use these AI features, the prompts, instructions, or other information you provide may be processed to generate the requested responses and deliver the intended functionality."
          },
          {
            "type": "paragraph",
            "text": "AI processing is performed solely to provide and improve the AI-powered features available within the Cullath platform. We do not use AI to make automated decisions that produce legal or similarly significant effects on users."
          },
          {
            "type": "paragraph",
            "text": "Users are responsible for reviewing and verifying all AI-generated content before relying on or using it for business, legal, financial, medical, or other important decisions. While we strive to provide accurate and helpful AI-generated responses, their accuracy, completeness, and suitability cannot be guaranteed."
          },
          {
            "type": "paragraph",
            "text": "We recommend that users avoid submitting confidential, highly sensitive, or regulated information through AI features unless appropriate organizational policies, security measures, and legal requirements permit such use."
          }
        ]
      },
      {
        "id": "cookies-tracking-technologies",
        "number": "5",
        "title": "Cookies & Tracking Technologies",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath uses cookies and similar tracking technologies to enhance your experience, improve the performance of our Services, and better understand how our platform is used. Cookies are small text files stored on your device that help websites recognize your browser and remember certain information."
          },
          {
            "type": "paragraph",
            "text": "We may use the following types of cookies:"
          },
          {
            "type": "subheading",
            "text": "Essential Cookies"
          },
          {
            "type": "paragraph",
            "text": "These cookies are necessary for the operation of the Cullath platform. They enable core functionality such as user authentication, account security, session management, and access to essential features. Without these cookies, certain parts of the Services may not function properly."
          },
          {
            "type": "subheading",
            "text": "Analytics Cookies"
          },
          {
            "type": "paragraph",
            "text": "Analytics cookies help us understand how users interact with our website and Services. They provide information about website traffic, feature usage, and overall platform performance, allowing us to improve functionality and enhance the user experience."
          },
          {
            "type": "subheading",
            "text": "Preference Cookies"
          },
          {
            "type": "paragraph",
            "text": "Preference cookies remember your settings and preferences, such as language, interface preferences, and other personalized options, helping provide a more consistent and customized experience during future visits."
          },
          {
            "type": "paragraph",
            "text": "You can control or disable cookies through your browser settings. Please note that disabling certain cookies may affect the functionality or performance of some features within the Cullath platform."
          }
        ]
      },
      {
        "id": "data-sharing-third-parties",
        "number": "6",
        "title": "Data Sharing & Third Parties",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath respects your privacy and does not sell, rent, or trade your personal information to third parties for marketing or commercial purposes."
          },
          {
            "type": "paragraph",
            "text": "We may share your information only in the following circumstances:"
          },
          {
            "type": "subheading",
            "text": "Service Providers"
          },
          {
            "type": "paragraph",
            "text": "We may share information with trusted third-party service providers who assist us in operating, maintaining, securing, or improving the Cullath platform. These providers are authorized to process information only as necessary to perform services on our behalf and are required to protect your information in accordance with applicable privacy and security requirements."
          },
          {
            "type": "subheading",
            "text": "Legal Requirements"
          },
          {
            "type": "paragraph",
            "text": "We may disclose your information when required to do so by applicable law, regulation, legal process, court order, or a valid request from a government or regulatory authority. We may also disclose information where necessary to protect our rights, investigate suspected fraud, enforce our Terms & Conditions, or safeguard the security of our Services and users."
          },
          {
            "type": "subheading",
            "text": "Business Transfers"
          },
          {
            "type": "paragraph",
            "text": "If Cullath is involved in a merger, acquisition, corporate restructuring, financing, sale of assets, or other business transaction, your information may be transferred as part of that transaction, subject to applicable legal and contractual obligations. We will take reasonable steps to ensure that your information continues to be protected in accordance with this Privacy Policy."
          },
          {
            "type": "paragraph",
            "text": "Except as described in this Privacy Policy, Cullath will not disclose your personal information to third parties without your consent unless required or permitted by applicable law."
          }
        ]
      },
      {
        "id": "data-security",
        "number": "7",
        "title": "Data Security",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath is committed to protecting the security of your personal information. We implement reasonable administrative, technical, and organizational measures designed to safeguard the information we collect against unauthorized access, disclosure, alteration, misuse, or destruction."
          },
          {
            "type": "paragraph",
            "text": "Our security practices are intended to support the confidentiality, integrity, and availability of the Cullath platform. These measures may include access controls, user authentication, data encryption where appropriate, secure communication protocols, system monitoring, and regular maintenance of our Services."
          },
          {
            "type": "paragraph",
            "text": "While we take reasonable steps to protect your information, no method of electronic transmission or data storage is completely secure. As a result, we cannot guarantee absolute security. Users are encouraged to maintain the security of their account credentials and promptly report any suspected unauthorized access or security incidents."
          },
          {
            "type": "paragraph",
            "text": "As Cullath continues to grow, we will regularly review and enhance our security practices to support the evolving needs of our customers and the platform."
          }
        ]
      },
      {
        "id": "data-retention",
        "number": "8",
        "title": "Data Retention",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath retains personal information only for as long as necessary to provide our Services, fulfill the purposes described in this Privacy Policy, comply with applicable legal obligations, resolve disputes, enforce our agreements, and support legitimate business operations."
          },
          {
            "type": "paragraph",
            "text": "The length of time we retain information depends on the type of data, the nature of the Services provided, and applicable legal or regulatory requirements. When personal information is no longer required for these purposes, we will take reasonable steps to securely delete, anonymize, or otherwise dispose of it in accordance with our data retention practices."
          },
          {
            "type": "paragraph",
            "text": "If you choose to close your account, certain information may be retained for a limited period where required to comply with legal obligations, resolve disputes, prevent fraud, enforce our Terms & Conditions, or protect the security and integrity of the Cullath platform."
          }
        ]
      },
      {
        "id": "your-privacy-rights",
        "number": "9",
        "title": "Your Privacy Rights",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath respects your privacy and is committed to providing you with appropriate control over your personal information. Depending on your location and applicable laws, you may have certain rights regarding the personal data we collect and process."
          },
          {
            "type": "paragraph",
            "text": "These rights may include the ability to:"
          },
          {
            "type": "list",
            "items": [
              "Access the personal information we hold about you.",
              "Correct or update inaccurate or incomplete personal information.",
              "Request the deletion of your personal information, subject to applicable legal or contractual obligations.",
              "Request a copy or export of your personal information, where technically feasible and permitted by applicable law."
            ]
          },
          {
            "type": "paragraph",
            "text": "To exercise any of these rights, or if you have questions regarding your personal information, you may contact us using the details provided in the Contact Us section of this Privacy Policy."
          },
          {
            "type": "paragraph",
            "text": "We will review and respond to your request within a reasonable timeframe and in accordance with applicable laws and regulations."
          }
        ]
      },
      {
        "id": "third-party-links",
        "number": "10",
        "title": "Third-Party Links",
        "blocks": [
          {
            "type": "paragraph",
            "text": "The Cullath platform and website may contain links to third-party websites, applications, or services that are provided for your convenience or to enhance your experience. These third-party services operate independently of Cullath and are governed by their own privacy policies and terms of use."
          },
          {
            "type": "paragraph",
            "text": "Cullath is not responsible for the privacy practices, content, security, or policies of any third-party websites or services. We encourage you to review the privacy policies of any third-party websites or services you visit before providing any personal information."
          },
          {
            "type": "paragraph",
            "text": "The inclusion of a third-party link does not imply endorsement, approval, or responsibility for the products, services, or content provided by those third parties."
          }
        ]
      },
      {
        "id": "changes-to-this-privacy-policy",
        "number": "11",
        "title": "Changes to This Privacy Policy",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath may update or modify this Privacy Policy from time to time to reflect changes in our Services, technologies, legal requirements, or business practices."
          },
          {
            "type": "paragraph",
            "text": "When we make material changes to this Privacy Policy, we will update the \"Last Updated\" date at the top of this page and, where appropriate, provide additional notice through our website or the Cullath platform."
          },
          {
            "type": "paragraph",
            "text": "We encourage you to review this Privacy Policy periodically to stay informed about how we collect, use, and protect your information. Your continued use of the Cullath Services after any changes become effective constitutes your acceptance of the updated Privacy Policy."
          }
        ]
      },
      {
        "id": "contact-us",
        "number": "12",
        "title": "Contact Us",
        "blocks": [
          {
            "type": "paragraph",
            "text": "If you have any questions, concerns, requests, or feedback regarding this Privacy Policy or how Cullath collects, uses, stores, or protects your personal information, please contact us using the details below:"
          },
          {
            "type": "paragraph",
            "text": "Company Name Cullath "
          },
          {
            "type": "paragraph",
            "text": "Email support@cullath.com"
          },
          {
            "type": "paragraph",
            "text": "Registered Address  : JK Embassy 3, Sunderamurthy Rd, Cox Town, Bengaluru, Karnataka 560005"
          },
          {
            "type": "paragraph",
            "text": "Website https://cullath.com"
          },
          {
            "type": "paragraph",
            "text": "We will make reasonable efforts to respond to your inquiries and privacy-related requests in a timely manner and in accordance with applicable laws."
          }
        ]
      }
    ]
  },
  "terms": {
    "title": "Terms & Conditions",
    "effective": "Effective Date: July 2026",
    "updated": "Last Updated: July 2026",
    "sections": [
      {
        "id": "introduction",
        "number": "1",
        "title": "Introduction",
        "blocks": [
         
 
 {
  "type": "paragraph",
  "content": [
    { "type": "text", "value": "Welcome to " },
{ "type": "link", "value": "Cullath", "href": "https://cullath.com" },
{ "type": "text", "value": ". These Cullath Terms & Conditions (\"Terms\") govern your access to and use of the Cullath connected workplace platform, including " },
{ "type": "link", "value": "Cullath Connect", "href": "https://cullath.com/connect" },
{ "type": "text", "value": ", " },
{ "type": "link", "value": "Cullath Desk", "href": "https://cullath.com/desk" },
{ "type": "text", "value": ", " },
{ "type": "link", "value": "Cullath Assist", "href": "https://cullath.com/assist" },
{ "type": "text", "value": ", our website, applications, and related services (collectively, the \"Services\")." }]
},
          {
            "type": "paragraph",
            "text": "By accessing, registering for, or using any Cullath Services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree with any part of these Terms, you should not access or use our Services."
          },
          {
            "type": "paragraph",
            "text": "These Terms apply to all users, including individuals, businesses, organizations, administrators, and any authorized users accessing the Cullath platform."
          }
        ]
      },
      {
        "id": "acceptance-of-terms",
        "number": "2",
        "title": "Acceptance of Terms",
        "blocks": [
          {
            "type": "paragraph",
            "text": "By accessing or using the Cullath platform, you confirm that you have read, understood, and agree to comply with these Terms & Conditions, as well as any additional policies referenced within them."
          },
          {
            "type": "paragraph",
            "text": "If you are using Cullath on behalf of a company, organization, or other legal entity, you represent and warrant that you have the authority to bind that entity to these Terms. In such cases, the terms \"you\" and \"your\" refer to both the individual user and the organization they represent."
          },
          {
            "type": "paragraph",
            "text": "If you do not agree to these Terms, you must discontinue use of the Cullath platform and its Services immediately."
          }
        ]
      },
      {
        "id": "user-accounts",
        "number": "3",
        "title": "User Accounts",
        "blocks": [
          {
            "type": "paragraph",
            "text": "To access certain features of the Cullath platform, you may be required to create a user account. You agree to provide accurate, complete, and up-to-date information during the registration process and to keep your account information current."
          },
          {
            "type": "paragraph",
            "text": "You are responsible for maintaining the confidentiality of your account credentials, including your username and password. You are also responsible for all activities that occur under your account, whether authorized by you or not."
          },
          {
            "type": "paragraph",
            "text": "If you believe your account has been accessed without authorization or your login credentials have been compromised, you must notify Cullath immediately and take appropriate steps to secure your account."
          },
          {
            "type": "paragraph",
            "text": "Cullath reserves the right to suspend or terminate accounts that contain false information, violate these Terms, or are used in a manner that may compromise the security, integrity, or availability of the platform."
          }
        ]
      },
      {
        "id": "acceptable-use",
        "number": "4",
        "title": "Acceptable Use",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath is designed to provide a secure, reliable, and professional collaboration platform for businesses and teams. By using our Services, you agree to use the platform responsibly and in compliance with all applicable laws and regulations."
          },
          {
            "type": "paragraph",
            "text": "You agree not to:"
          },
          {
            "type": "list",
            "items": [
              "Use the Cullath platform for any unlawful, fraudulent, or unauthorized activities.",
              "Attempt to gain unauthorized access to any accounts, systems, networks, or data.",
              "Upload, distribute, or transmit viruses, malware, ransomware, or any other harmful code that may disrupt or damage the platform or its users.",
              "Interfere with, disrupt, or compromise the security, integrity, or performance of the Cullath Services.",
              "Harass, abuse, threaten, impersonate, or otherwise harm other users or organizations using the platform.",
              "Reverse engineer, decompile, copy, modify, or attempt to extract the source code or underlying technology of the Cullath platform, except where permitted by applicable law.",
              "Use the platform to distribute spam, unsolicited communications, or any content that is illegal, offensive, or infringes on the rights of others.",
              "Use the Services in any manner that could negatively affect the availability, reliability, or operation of the platform."
            ]
          },
          {
            "type": "paragraph",
            "text": "Cullath reserves the right to investigate suspected violations of these Terms and may suspend or terminate accounts that engage in prohibited activities, without prior notice where necessary to protect the platform, its users, or applicable legal obligations."
          }
        ]
      },
      {
        "id": "ai-features",
        "number": "5",
        "title": "AI Features",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath provides AI-powered features to help users improve productivity, streamline communication, and assist with tasks such as drafting content, summarizing information, and generating suggestions across the platform."
          },
          {
            "type": "paragraph",
            "text": "While we strive to provide helpful and accurate AI-generated outputs, such content is generated automatically and may not always be complete, accurate, or suitable for every situation. Users are responsible for reviewing, verifying, and evaluating all AI-generated content before relying on or using it for business, legal, financial, medical, or other important decisions."
          },
          {
            "type": "paragraph",
            "text": "You acknowledge and agree that you are solely responsible for how AI-generated content is used, modified, shared, or implemented. Cullath does not guarantee the accuracy, completeness, or reliability of AI-generated outputs and shall not be held liable for decisions or actions taken based on such content."
          },
          {
            "type": "paragraph",
            "text": "Cullath may improve, modify, or update its AI features from time to time to enhance performance, functionality, and user experience."
          }
        ]
      },
      {
        "id": "intellectual-property",
        "number": "6",
        "title": "Intellectual Property",
        "blocks": [
          {
            "type": "paragraph",
            "text": "All rights, title, and interest in and to the Cullath platform, including its software, applications, website, features, designs, user interface, source code, technology, logos, trademarks, branding, graphics, documentation, and other intellectual property, are owned by or licensed to Cullath and are protected by applicable intellectual property laws."
          },
          {
            "type": "paragraph",
            "text": "Your use of the Cullath platform does not grant you ownership of any intellectual property rights. Except as expressly permitted by these Terms, you may not copy, reproduce, modify, distribute, sell, lease, sublicense, reverse engineer, or otherwise exploit any part of the Cullath platform without our prior written consent."
          },
          {
            "type": "paragraph",
            "text": "Subject to your compliance with these Terms, Cullath grants you a limited, non-exclusive, non-transferable, and revocable license to access and use the Services solely for your internal business or personal use in accordance with these Terms."
          },
          {
            "type": "paragraph",
            "text": "Any unauthorized use of the Cullath platform or its intellectual property may result in the suspension or termination of your access and may be subject to applicable legal remedies."
          }
        ]
      },
      {
        "id": "privacy",
        "number": "7",
        "title": "Privacy",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Cullath is committed to protecting the privacy and security of your personal information. Any personal data collected, processed, or stored through your use of the Cullath platform is handled in accordance with our Privacy Policy."
          },
          {
            "type": "paragraph",
            "text": "Our Privacy Policy explains how we collect, use, store, share, and protect your information, as well as the choices and rights available to you regarding your personal data."
          },
          {
            "type": "paragraph",
            "text": "By using the Cullath platform, you acknowledge that your personal information may be collected and processed as described in our Privacy Policy. We encourage you to review the Privacy Policy carefully to understand our data handling practices."
          }
        ]
      },
      {
        "id": "termination",
        "number": "8",
        "title": "Termination",
        "blocks": [
          {
            "type": "paragraph",
            "text": "You may discontinue your use of the Cullath platform and terminate your account at any time, subject to any applicable subscription or contractual obligations."
          },
          {
            "type": "paragraph",
            "text": "Cullath reserves the right to suspend, restrict, or terminate your access to the platform, with or without prior notice, if we reasonably believe that you have violated these Terms & Conditions, engaged in unauthorized or unlawful activities, compromised the security or integrity of the platform, or otherwise misused the Services."
          },
          {
            "type": "paragraph",
            "text": "Upon termination of your account, your right to access and use the Cullath Services will immediately cease. Cullath may retain or delete account data in accordance with applicable laws, our Privacy Policy, and internal data retention practices."
          },
          {
            "type": "paragraph",
            "text": "Termination of your account does not affect any rights, obligations, or liabilities that arose prior to the date of termination, including provisions relating to intellectual property, limitation of liability, and any other clauses that are intended to survive termination."
          }
        ]
      },
      {
        "id": "limitation-of-liability",
        "number": "9",
        "title": "Limitation of Liability",
        "blocks": [
          {
            "type": "paragraph",
            "text": "To the fullest extent permitted by applicable law, Cullath, its affiliates, directors, employees, partners, and licensors shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of, or inability to use, the Cullath platform or its Services."
          },
          {
            "type": "paragraph",
            "text": "While we strive to provide a reliable, secure, and uninterrupted platform, Cullath does not guarantee that the Services will always be error-free, uninterrupted, or available at all times. You acknowledge that temporary interruptions, maintenance activities, technical issues, or circumstances beyond our reasonable control may affect the availability or performance of the Services."
          },
          {
            "type": "paragraph",
            "text": "You agree that your use of the Cullath platform is at your own risk and that Cullath's total liability for any claim relating to the Services shall be limited to the maximum extent permitted under applicable law."
          },
          {
            "type": "paragraph",
            "text": "Nothing in these Terms excludes or limits liability where such exclusion or limitation is prohibited by applicable law."
          }
        ]
      },
      {
        "id": "changes-to-these-terms",
        "number": "10",
        "title": "Changes to These Terms",
        "blocks": [
          {
            "type": "paragraph",
            "text": "We may update or modify these Terms & Conditions from time to time to reflect changes to our Services, legal requirements, or business practices."
          },
          {
            "type": "paragraph",
            "text": "When material changes are made, we will update the \"Last Updated\" date on this page and, where appropriate, provide additional notice."
          },
          {
            "type": "paragraph",
            "text": "Your continued use of the Cullath platform after any changes become effective constitutes your acceptance of the revised Terms."
          }
        ]
      },
      {
        "id": "contact-us",
        "number": "11",
        "title": "Contact Us",
        "blocks": [
          {
            "type": "paragraph",
            "text": "If you have any questions, concerns, or requests regarding these Terms & Conditions, or if you require additional information about the Cullath platform, please contact us using the details below:"
          },
          {
            "type": "paragraph",
            "text": "Company Name Cullath (Legal Entity Name – To Be Updated)"
          },
          {
            "type": "paragraph",
            "text": "Email support@cullath.com (to be create)"
          },
          {
            "type": "paragraph",
            "text": "Registered Address To Be Updated"
          },
          {
            "type": "paragraph",
            "text": "Website https://cullath.com"
          },
          {
            "type": "paragraph",
            "text": "We will make reasonable efforts to respond to your inquiries in a timely manner."
          }
        ]
      }
    ]
  }
};
